import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useState } from 'react';

// Simple X icon for clear button
const XIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

interface SearchBarProps {
  /**
   * Current search value
   */
  search: string;

  /**
   * Function to update search value
   */
  setSearch: (value: string) => void;

  /**
   * Placeholder text for the search input
   */
  placeholder?: string;

  /**
   * Debounce delay in milliseconds
   */
  debounceMs?: number;

  /**
   * Optional ARIA label for accessibility
   */
  ariaLabel?: string;

  /**
   * Additional CSS classes for the container (including width)
   * Defaults to 'w-64' if not provided
   */
  className?: string;
}

/**
 * A reusable search bar component that can be used across different tables.
 */
export const SearchBar: React.FC<SearchBarProps> = ({
  search,
  setSearch,
  placeholder = 'Search',
  debounceMs = 500,
  ariaLabel,
  className = 'w-64',
}) => {
  const [localValue, setLocalValue] = useState(search);

  // Create a debounced version of setSearch
  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearch(value), debounceMs),
    [setSearch, debounceMs]
  );

  // Update local value when URL value changes
  useEffect(() => {
    setLocalValue(search);
  }, [search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);
    debouncedSearch(value);
  };

  const handleClear = () => {
    setLocalValue('');
    debouncedSearch('');
  };

  const hasValue = localValue.length > 0;

  return (
    <div className={`relative ${className}`}>
      {/* Search icon */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3 text-gray-400 dark:text-gray-600">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Clear button */}
      {hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute inset-y-0 right-0 z-10 flex items-center pr-3 text-gray-600 focus:outline-none"
          aria-label="Clear search">
          <XIcon />
        </button>
      )}

      <input
        type="text"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        className={`input input-bordered h-9 w-full pl-9 text-sm ${hasValue ? 'pr-9' : 'pr-3'}`}
        aria-label={ariaLabel}
      />
    </div>
  );
};
